/* eslint-disable */
/**
 * Client 子路由 ↔ 菜单码（menuCodes）映射
 *
 * 用途：`ClientOutletGuard` 根据当前 URL 判断用户是否具备进入该页的菜单权限，
 * 防止「侧栏已隐藏但手输地址仍可进」。
 *
 * 数据来源：仅扫描 `AppHeader/menus.js`，与侧栏展示同源；不在菜单树里出现的路径
 * 或菜单项未配置 `menuCode` / `menuCodes` / `roles` 的，映射表中无记录，
 * 守卫层视为「不做菜单码拦截」（与 `isMenuItemVisible` 中「无码则全员可见」一致）。
 *
 * 路径约定：`key` 形如 `/client/order/orderList`，映射键为去掉前缀后的相对段 `order/orderList`，
 * 与 `react-router` 下 `/client/*` 子路由的 path 片段对齐。
 */
import menus from '@/components/AppHeader/menus'
import { getMenuRBACCodes } from '@/utils/menuItemVisible'

/**
 * 扁平化菜单树，构建「相对路径 → 所需菜单码数组」。
 * 仅当该项存在 `key` 且以 `/client/` 开头，且 `getMenuRBACCodes` 非空时写入。
 */
function buildRelativePathToMenuCodes () {
  const map = Object.create(null)

  const walk = (items) => {
    if (!Array.isArray(items)) return
    for (const item of items) {
      const key = item && item.key
      if (typeof key === 'string' && key.startsWith('/client/')) {
        // 与 Route path 对齐：无首尾斜杠，如 order/orderList
        const rel = key.replace(/^\/client\/?/, '').replace(/\/+$/, '')
        const codes = getMenuRBACCodes(item)
        if (codes && codes.length > 0) {
          map[rel] = codes
        }
      }
      if (item && item.children) {
        walk(item.children)
      }
    }
  }

  walk(menus)
  return map
}

/** 模块加载时构建一次，避免每次导航重复遍历菜单树 */
const RELATIVE_PATH_TO_MENU_CODES = buildRelativePathToMenuCodes()

/** 与 `menus.js` 中「派单」/client/order/dispatch 同源，新订单·手机端与 PC 进派单需同一组 menuCodes */
const DISPATCH_PATH_MENU_CODES = RELATIVE_PATH_TO_MENU_CODES['order/dispatch'] || null

/**
 * 未在侧栏出现的子路由，进入条件与某主路由一致
 * @type {Object.<string, string[]>}
 */
const EXTRA_RELATIVE_PATH_TO_MENU_CODES = {
  // 同 PC 派单 → 新订单（未分配产康排期）能力，与 /client/order/dispatch 一致
  'order/dispatchNewOrdersMobile':
    Array.isArray(DISPATCH_PATH_MENU_CODES) && DISPATCH_PATH_MENU_CODES.length
      ? [...DISPATCH_PATH_MENU_CODES]
      : ['order:dispatcher']
}

/**
 * 将浏览器 pathname 转为 `/client` 之后的相对路径，供查表。
 *
 * @param {string} pathname - 如 `/client/order/orderList` 或 `/client/order/orderList/`
 * @returns {string|null} 如 `order/orderList`；非 `/client` 下返回 `null`
 */
export function clientPathnameToRelative (pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/client')) {
    return null
  }
  let rest = pathname.slice('/client'.length)
  rest = rest.replace(/^\/+/, '').replace(/\/+$/, '')
  return rest
}

/**
 * 查询进入该相对路径所需的菜单码（命中任意一个即视为有菜单权限）。
 *
 * @param {string} relativePath - 如 `order/orderList`
 * @returns {string[]|null} 所需码列表；`null` 表示映射表无要求，路由守卫放行
 */
export function getRequiredMenuCodesForClientPath (relativePath) {
  if (relativePath == null || relativePath === '') {
    return null
  }
  const required =
    RELATIVE_PATH_TO_MENU_CODES[relativePath] || EXTRA_RELATIVE_PATH_TO_MENU_CODES[relativePath]
  if (!required || !Array.isArray(required) || required.length === 0) {
    return null
  }
  return required
}
